<template>
  <div>
    <Card
      v-bind="$attrs"
      :clickable="isClickable"
      :selected="selected"
      :href="profileUrl"
      :target="target"
      :custom-classes="customClasses"
    >
      <AlertBanner v-if="availability" :status="validStatus" :auto-scroll-to-banner="false">
        <template #icon>
          <CalendarIcon />
        </template>
        Available: {{ availabilityText }}
      </AlertBanner>
      <AlertBanner v-if="shouldShowAvailabilityAlert" class="mt-8 mb-16" :status="6" :auto-scroll-to-banner="false">
        <div>You invited this sitter to apply</div>
      </AlertBanner>
      <div v-if="isLoading"><SitterCardLoadingSkeleton /></div>
      <div v-else>
        <div class="flex flex-col">
          <div class="w-full flex flex-row">
            <div class="w-full pr-16 overflow-hidden">
              <h2 class="text-h3 text-ellipsis overflow-hidden whitespace-nowrap">{{ name }}</h2>
              <p v-if="professionalExperienceDisplay" class="text-body">{{ professionalExperienceDisplay }}</p>
              <p v-if="informalExperienceDisplay" class="text-body">{{ informalExperienceDisplay }}</p>
              <p class="text-body">{{ locationText }}</p>
              <p v-if="hasRate" class="text-body">
                <span class="profile-card__rate-unit">$</span>
                <span class="profile-card__rate-value">{{ computedMin }}–{{ computedMax }}</span
                >/hr
              </p>
              <StarRating v-if="totalRatings > 0" :count="totalRatings || 0" :average="avgRating" />
            </div>
            <div>
              <RouterLink
                v-if="photoUploadAvatar"
                class="relative"
                aria-label="Photo upload"
                :to="{ name: 'provider-photo-upload', query: { profileType: photoProfileType } }"
              >
                <Avatar photo-upload-icon :size="AvatarSize.MEGA" name="" empty-alt />
                <div class="flex justify-center items-center w-32 h-32 absolute top-14 right-0" aria-label="Add profile photo">
                  <AddFilled />
                </div>
              </RouterLink>
              <Avatar v-else :name="name" :image-url="photoUrl" :size="AvatarSize.MEGA" />
            </div>
          </div>
          <p
            v-if="truncatedDescription"
            id="sitter-card-description"
            class="text-small mt-16 border-t-2 border-solid border-grey-divider-subdued pt-4"
          >
            {{ truncatedDescription }}
          </p>
          <div
            class="flex gap-y-12 md:gap-16 mt-16"
            :class="$slots.actions ? 'flex-col items-start gap-x-16 md:flex-row md:items-center' : 'flex-wrap items-center'"
          >
            <SitterBadgeList :include-inactive-badges="false" :badge-size="40" :idv="idv" :badges="visibleBadges" />
            <Button
              v-if="shouldShowInviteButton"
              value="Invite to Apply"
              variant="primary"
              size="small"
              class="whitespace-nowrap ml-auto max-[410px]:ml-0 max-[410px]:w-full"
              @click="openInvitePopup"
            />
            <div v-if="$slots.actions" class="flex shrink-0 items-center gap-16 md:ml-auto">
              <slot name="actions" />
            </div>
          </div>
        </div>
      </div>
    </Card>
    <!-- Popup is outside Card to prevent click propagation issues -->
    <RecruitSitterPopup
      v-if="account && (shouldShowInviteButton || showInvitePopup)"
      v-model="showInvitePopup"
      :account="account"
      :sitter-uuid="sitterIdForProfile"
      :sitter-name="name"
      :search-care-type="searchCareType || caretype"
      :parent-jobs="parentJobs"
      @sitter-recruited="handleSitterRecruited"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { parseISO, format } from 'date-fns';
import pluralize from 'pluralize';
import { CareType } from '@/types/care_type';
import helperStatus from '@/helpers/status';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import AlertBanner from '../alert_banner/AlertBanner.vue';
import Avatar, { AvatarSize } from '../avatar/Avatar.vue';
import Card from '../card/Card.vue';
import SitterCardLoadingSkeleton from './SitterCardLoadingSkeleton.vue';
import StarRating from '../star_rating/StarRating.vue';
import SitterBadgeList from '../sitter_badge_list/SitterBadgeList.vue';
import Button from '../button/Button.vue';
import RecruitSitterPopup from '@/apps/search/features/RecruitSitterPopup.vue';
import CalendarIcon from '../../icons/CalendarIcon.vue';
import AddFilled from '../../icons/AddFilled.vue';
import type { IDV, Badge } from '../sitter_badge_list/__types__/SitterBadgeListTypes';
import type { LinkTarget } from '@/helpers/redirects';
import type { Account } from '@/models/account';
import type { JobEdge } from '@/graphql/search/queries';

defineOptions({ name: 'SitterCard', inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    name?: string;
    city?: string;
    stateAbbr?: string;
    description?: string | null;
    avgRating?: number | null;
    minRate?: number | null;
    maxRate?: number | null;
    distance?: number | null;
    totalRatings?: number | null;
    photoUrl?: string;
    yearsExperience?: number | null;
    informalYearsExperience?: number | null;
    idv?: Partial<IDV> | null;
    badges?: Badge[];
    sitterIdForProfile?: string | null;
    caretype?: string;
    availability?: { start: string; end: string } | null;
    isLoading?: boolean;
    customClasses?: string;
    photoUploadAvatar?: boolean;
    photoProfileType?: CareType;
    target?: LinkTarget;
    account?: Account | null;
    showInviteButton?: boolean;
    isRecruited?: boolean;
    searchCareType?: string;
    parentJobs?: JobEdge[];
    selected?: boolean;
  }>(),
  {
    name: '',
    city: '',
    stateAbbr: '',
    description: '',
    avgRating: null,
    minRate: null,
    maxRate: null,
    distance: null,
    totalRatings: null,
    photoUrl: '',
    yearsExperience: null,
    informalYearsExperience: null,
    idv: () => ({}),
    badges: () => [],
    sitterIdForProfile: null,
    caretype: 'babysitter',
    availability: null,
    isLoading: false,
    customClasses: '',
    photoUploadAvatar: false,
    photoProfileType: CareType.BABYSITTER,
    target: '_self',
    account: null,
    showInviteButton: false,
    isRecruited: false,
    searchCareType: '',
    parentJobs: () => [],
    selected: false,
  }
);
const emit = defineEmits<{ sitterRecruited: [sitterUuid: string | null] }>();

const METERS_TO_MILES_CONVERSION = 1609.344;
const LIMIT_WORD_COUNT = 300;
const instance = getCurrentInstance();
const showInvitePopup = ref(false);
const locallyRecruited = ref(false);

const effectiveIsRecruited = computed(() => props.isRecruited || locallyRecruited.value);
const isClickable = computed(() => !!props.sitterIdForProfile);
const isAvailabilityRoute = computed(() => instance?.proxy?.$route?.path === '/availability/search');
const shouldShowAvailabilityAlert = computed(
  () => effectiveIsRecruited.value && (isAvailabilityRoute.value || props.showInviteButton)
);
const isChildCareProfile = computed(() => props.caretype === 'babysitter' || props.caretype === 'nanny');
const professionalExperienceDisplay = computed(() => {
  const experience = props.yearsExperience ?? 0;
  if (isChildCareProfile.value) {
    return experience < 1 ? '' : `${experience} ${pluralize('year', experience)} professional experience`;
  }
  return experience ? `${experience} ${pluralize('year', experience)} experience` : '';
});
const informalExperienceDisplay = computed(() => {
  const experience = props.informalYearsExperience ?? 0;
  return isChildCareProfile.value && experience >= 1 ? `${experience} ${pluralize('yr', experience)} informal experience` : '';
});
const computedDistance = computed(() => {
  if (typeof props.distance !== 'number') return '';
  const miles = Math.round(props.distance / METERS_TO_MILES_CONVERSION);
  return miles < 1 ? '< 1 mile' : `${miles} ${pluralize('mile', miles)}`;
});
const locationText = computed(() => {
  const cityState = [props.city, props.stateAbbr].filter(Boolean).join(', ');
  return [cityState, computedDistance.value].filter(Boolean).join(' • ');
});
const truncatedDescription = computed(() => {
  if (!props.description) return '';
  const regex = new RegExp(`^(.{${LIMIT_WORD_COUNT}}[^\\s]*).*`);
  return `${props.description.replace(/(\r\n|\n|\r)/gm, '').replace(regex, '$1')}...`;
});
const computedMin = computed(() => Math.floor((props.minRate ?? 0) / 100));
const computedMax = computed(() => Math.ceil((props.maxRate ?? 0) / 100));
const hasRate = computed(() => computedMin.value > 0 && computedMax.value > 0);
const profileUrl = computed(() => getProviderProfileUrl(props.sitterIdForProfile, props.caretype));
const availabilityText = computed(() =>
  props.availability
    ? `${format(parseISO(props.availability.start), 'MMM d, h:mm aaa')} – ${format(parseISO(props.availability.end), 'h:mm aaa')}`
    : ''
);
const validStatus = helperStatus.Valid;
const visibleBadges = computed(() =>
  props.badges.every((badge) => badge.status === 'COMPLETE') && props.idv?.verdict === 'passed'
    ? props.badges.filter((badge) => badge.name !== 'basic_criminal')
    : props.badges
);
const shouldShowInviteButton = computed(() => props.showInviteButton && !effectiveIsRecruited.value && !!props.account);

function openInvitePopup(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  showInvitePopup.value = true;
}
function handleSitterRecruited(sitterUuid: string | null = null) {
  locallyRecruited.value = true;
  emit('sitterRecruited', sitterUuid || props.sitterIdForProfile || null);
}
</script>

<style lang="scss" scoped>
#sitter-card-description {
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
</style>
